import { useCallback, useEffect, useState } from 'react';
import '../styles/adminWorkspace.css';
import { getStaff, inviteStaff, resendStaffInvite, getLoginActivity } from '../services/adminService';

// ─── constants ────────────────────────────────────────────────────────────────

const ROLES = [
  { value: 'admin',       label: 'Admin',       desc: 'Full platform access' },
  { value: 'sales',       label: 'Sales',        desc: 'Sales ops workspace' },
  { value: 'mentor-lead', label: 'Mentor Lead',  desc: 'Mentor ops workspace' },
  { value: 'teacher',     label: 'Mentor',       desc: 'Classroom + sessions' },
];

const ROLE_FILTER_TABS = [
  { value: 'all',         label: 'All' },
  { value: 'admin',       label: 'Admins' },
  { value: 'sales',       label: 'Sales' },
  { value: 'mentor-lead', label: 'Mentor Leads' },
  { value: 'teacher',     label: 'Mentors' },
];

const ROLE_LABELS = {
  admin:        'Admin',
  sales:        'Sales',
  'mentor-lead':'Mentor Lead',
  teacher:      'Mentor',
};

// ─── helpers ──────────────────────────────────────────────────────────────────

function initials(name = '') {
  return name
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0].toUpperCase())
    .join('');
}

function LoadingDots() {
  return (
    <span className="aw-loading-dots" aria-label="Loading">
      <span /><span /><span />
    </span>
  );
}

// ─── InviteCard ───────────────────────────────────────────────────────────────

function InviteCard({ onInvited }) {
  const [form, setForm]   = useState({ firstName: '', lastName: '', email: '', role: 'admin' });
  const [status, setStatus] = useState({ loading: false, error: '', done: false, addedUser: null });

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus({ loading: true, error: '', done: false, addedUser: null });
    try {
      const data = await inviteStaff(form);
      setStatus({ loading: false, error: '', done: true, addedUser: data?.user || null });
      setForm({ firstName: '', lastName: '', email: '', role: 'admin' });
      onInvited?.();
    } catch (err) {
      setStatus({ loading: false, error: err.message || 'Unable to send invite.', done: false, addedUser: null });
    }
  };

  const handleAddAnother = () =>
    setStatus({ loading: false, error: '', done: false, addedUser: null });

  return (
    <div className="aw-card">
      <p className="eyebrow">Invite team member</p>

      {status.done ? (
        <div className="aw-success-banner">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor"
            strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="20 6 9 17 4 12" />
          </svg>
          <div className="aw-success-body">
            <p className="aw-success-title">
              Invite sent to {status.addedUser?.name || form.email}
            </p>
            <p className="aw-success-sub">
              They'll receive a welcome email with a link to set their password.
            </p>
          </div>
          <button className="secondary-btn compact" onClick={handleAddAnother}>
            Invite another
          </button>
        </div>
      ) : (
        <form className="aw-form" onSubmit={handleSubmit}>
          <div className="aw-field-row">
            <div className="aw-field">
              <label className="aw-label" htmlFor="aw-first-name">
                First name <span className="aw-required">*</span>
              </label>
              <input
                id="aw-first-name"
                className="aw-input"
                type="text"
                placeholder="First name"
                value={form.firstName}
                onChange={(e) => set('firstName', e.target.value)}
                required
              />
            </div>
            <div className="aw-field">
              <label className="aw-label" htmlFor="aw-last-name">Last name</label>
              <input
                id="aw-last-name"
                className="aw-input"
                type="text"
                placeholder="Last name"
                value={form.lastName}
                onChange={(e) => set('lastName', e.target.value)}
              />
            </div>
          </div>

          <div className="aw-field-row aw-field-row--email-role">
            <div className="aw-field">
              <label className="aw-label" htmlFor="aw-email">
                Email address <span className="aw-required">*</span>
              </label>
              <input
                id="aw-email"
                className="aw-input"
                type="email"
                placeholder="team@example.com"
                value={form.email}
                onChange={(e) => set('email', e.target.value)}
                required
              />
            </div>
            <div className="aw-field">
              <label className="aw-label" htmlFor="aw-role">
                Role <span className="aw-required">*</span>
              </label>
              <select
                id="aw-role"
                className="aw-input aw-select"
                value={form.role}
                onChange={(e) => set('role', e.target.value)}
                required
              >
                {ROLES.map((r) => (
                  <option key={r.value} value={r.value}>{r.label}</option>
                ))}
              </select>
            </div>
          </div>

          {status.error && <p className="error-text">{status.error}</p>}

          <button
            className="aw-primary-btn"
            type="submit"
            disabled={status.loading || !form.firstName || !form.email}
          >
            {status.loading ? <LoadingDots /> : 'Send invite'}
          </button>
        </form>
      )}
    </div>
  );
}

// ─── StaffRow ─────────────────────────────────────────────────────────────────

function StaffRow({ member, onResent }) {
  const [resendState, setResendState] = useState('idle'); // idle | sending | sent | error

  const handleResend = async () => {
    setResendState('sending');
    try {
      await resendStaffInvite(member.email);
      setResendState('sent');
    } catch {
      setResendState('error');
    }
  };

  const isPending = !member.passwordSet;
  const avatarColor = `aw-avatar--${member.role}`;

  return (
    <div className="aw-staff-row">
      <div className={`aw-avatar ${avatarColor}`}>{initials(member.name)}</div>

      <div className="aw-staff-info">
        <p className="aw-staff-name">{member.name}</p>
        <p className="aw-staff-email">{member.email}</p>
      </div>

      <div className="aw-staff-badges">
        <span className={`aw-role-badge aw-role-badge--${member.role}`}>
          {ROLE_LABELS[member.role] || member.role}
        </span>
        {isPending && <span className="aw-pending-badge">Pending</span>}
      </div>

      {isPending && (
        <div className="aw-staff-actions">
          {resendState === 'sent' ? (
            <span className="aw-resent-text">Sent</span>
          ) : resendState === 'error' ? (
            <button className="aw-link-btn" onClick={handleResend}>Retry</button>
          ) : (
            <button
              className="aw-link-btn"
              onClick={handleResend}
              disabled={resendState === 'sending'}
            >
              {resendState === 'sending' ? <LoadingDots /> : 'Resend invite'}
            </button>
          )}
        </div>
      )}
    </div>
  );
}

// ─── TeamCard ─────────────────────────────────────────────────────────────────

function TeamCard({ refreshKey }) {
  const [staff, setStaff]         = useState([]);
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState('');
  const [filterRole, setFilterRole] = useState('all');

  const load = useCallback(() => {
    setLoading(true);
    setError('');
    getStaff()
      .then((d) => setStaff(d?.staff || []))
      .catch((err) => setError(err.message || 'Unable to load team.'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load, refreshKey]);

  const visible = filterRole === 'all'
    ? staff
    : staff.filter((m) => m.role === filterRole);

  const pendingCount = staff.filter((m) => !m.passwordSet).length;

  return (
    <div className="aw-card">
      <div className="aw-card-head">
        <div>
          <p className="eyebrow">Team</p>
          {pendingCount > 0 && (
            <p className="aw-pending-note">
              {pendingCount} pending invitation{pendingCount !== 1 ? 's' : ''}
            </p>
          )}
        </div>
        <button className="aw-refresh-btn" onClick={load} disabled={loading}>
          Refresh
        </button>
      </div>

      {/* Filter tabs */}
      <div className="aw-filter-tabs" role="tablist">
        {ROLE_FILTER_TABS.map((t) => (
          <button
            key={t.value}
            type="button"
            role="tab"
            aria-selected={filterRole === t.value}
            className={`aw-filter-tab ${filterRole === t.value ? 'active' : ''}`}
            onClick={() => setFilterRole(t.value)}
          >
            {t.label}
            {t.value !== 'all' && (
              <span className="aw-tab-count">
                {staff.filter((m) => m.role === t.value).length}
              </span>
            )}
          </button>
        ))}
      </div>

      <div className="aw-staff-list">
        {loading && <p className="aw-muted">Loading team…</p>}
        {!loading && error && <p className="error-text">{error}</p>}
        {!loading && !error && visible.length === 0 && (
          <p className="aw-muted">No members in this group yet.</p>
        )}
        {!loading && !error && visible.map((member) => (
          <StaffRow key={member.id} member={member} />
        ))}
      </div>
    </div>
  );
}

// ─── LoginActivityCard ────────────────────────────────────────────────────────

function relativeTime(dateStr) {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins  = Math.floor(diff / 60_000);
  const hours = Math.floor(diff / 3_600_000);
  const days  = Math.floor(diff / 86_400_000);
  if (mins  <  1) return 'just now';
  if (mins  < 60) return `${mins}m ago`;
  if (hours < 24) return `${hours}h ago`;
  if (days  <  7) return `${days}d ago`;
  return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function LoginActivityCard() {
  const [logins, setLogins]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState('');

  const load = useCallback(() => {
    setLoading(true);
    setError('');
    getLoginActivity(30)
      .then((d) => setLogins(d?.logins || []))
      .catch((err) => setError(err.message || 'Unable to load login activity.'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="aw-card">
      <div className="aw-card-head">
        <div>
          <p className="eyebrow">Login Activity</p>
          {!loading && !error && logins.length > 0 && (
            <p className="aw-pending-note" style={{ color: 'var(--ink-muted)' }}>
              Last {logins.length} sign-ins
            </p>
          )}
        </div>
        <button className="aw-refresh-btn" onClick={load} disabled={loading}>
          Refresh
        </button>
      </div>

      <div className="aw-activity-list">
        {loading && <p className="aw-muted">Loading…</p>}
        {!loading && error && <p className="error-text">{error}</p>}
        {!loading && !error && logins.length === 0 && (
          <p className="aw-muted">No logins recorded yet.</p>
        )}
        {!loading && !error && logins.map((entry) => (
          <div key={entry.id} className="aw-activity-row">
            <div className={`aw-avatar aw-avatar--${entry.user?.role || 'admin'}`}>
              {initials(entry.user?.name || '?')}
            </div>
            <div className="aw-staff-info">
              <p className="aw-staff-name">{entry.user?.name || 'Unknown user'}</p>
              <p className="aw-staff-email">{entry.user?.email || '—'}</p>
            </div>
            <div className="aw-activity-meta">
              {entry.user?.role && (
                <span className={`aw-role-badge aw-role-badge--${entry.user.role}`}>
                  {ROLE_LABELS[entry.user.role] || entry.user.role}
                </span>
              )}
              <span className="aw-activity-time">{relativeTime(entry.loginAt)}</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export default function AdminWorkspacePage() {
  const [refreshKey, setRefreshKey] = useState(0);

  return (
    <div className="admin-workspace-page">
      <div className="aw-panel-grid">
        <div className="aw-panel-col">
          <InviteCard onInvited={() => setRefreshKey((k) => k + 1)} />
          <LoginActivityCard />
        </div>
        <div className="aw-panel-col">
          <TeamCard refreshKey={refreshKey} />
        </div>
      </div>
    </div>
  );
}
